import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Navbar, Footer } from "../components/Layout";
import { toast, Toaster } from "sonner";
import { useText } from "../contexts/TextsContext";
import { Phone, Calendar, CreditCard, ArrowRight, ArrowLeft, Check, Briefcase, FileText, Building, Upload, X, FileCheck2 } from "lucide-react";
import { trackFormSubmit, trackVisitRequest } from "../lib/tracking";

const STEPS = ["mode", "activity", "bilan", "company", "review"];
// "quick" flow skips bilan; final step depends on type ("paid" or other)

export default function ReservationFlow() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const initialType = search.get("type") || "contact";
  const t = useText();

  const TYPES = {
    contact: { icon: Phone, label: t("reservation.type.contact.label"), desc: t("reservation.type.contact.desc") },
    visit: { icon: Calendar, label: t("reservation.type.visit.label"), desc: t("reservation.type.visit.desc") },
    paid: { icon: CreditCard, label: t("reservation.type.paid.label"), desc: t("reservation.type.paid.desc") },
  };

  const [property, setProperty] = useState(null);
  const [activityTypes, setActivityTypes] = useState([]);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [type, setType] = useState(initialType);
  const [flowMode, setFlowMode] = useState("full"); // "quick" or "full"

  const [form, setForm] = useState({
    // Step 1 — Activity
    activity_type: "", activity_details: "",
    // Step 2 — Bilan
    bilan_revenue: "", bilan_profit: "", bilan_employees: "", bilan_fiscal_year: "", bilan_notes: "",
    bilan_documents: [],
    // Step 3 — Company
    company_siret: "", company_legal_name: "", company_director: "", company_address: "", company_postal: "", company_city: "",
    // Final — contact
    full_name: "", email: "", phone: "",
    requested_surface: "", start_date: "", duration_months: "",
    message: "", preferred_visit_date: "",
  });

  useEffect(() => {
    api.get(`/properties/${id}`).then(r => setProperty(r.data));
    api.get("/activity-types").then(r => setActivityTypes(r.data)).catch(() => {});
  }, [id]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  // Steps logic: order depends on flowMode
  const visibleSteps = flowMode === "quick"
    ? ["mode", "activity", "company", "review"]
    : ["mode", "activity", "bilan", "company", "review"];

  const currentStep = visibleSteps[step];

  const canNext = () => {
    if (currentStep === "mode") return true; // type+flowMode always set
    if (currentStep === "activity") return form.activity_type !== "";
    if (currentStep === "bilan") return true; // optional
    if (currentStep === "company") return form.company_legal_name && form.company_siret;
    if (currentStep === "review") return form.full_name && form.email && form.phone;
    return true;
  };

  const next = () => setStep(s => Math.min(s + 1, visibleSteps.length - 1));
  const prev = () => setStep(s => Math.max(s - 1, 0));

  const uploadDoc = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload/reservation-doc", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(prev => ({ ...prev, bilan_documents: [...prev.bilan_documents, data] }));
      toast.success("Document ajouté");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec upload"); }
  };

  const removeDoc = (i) => setForm({ ...form, bilan_documents: form.bilan_documents.filter((_, idx) => idx !== i) });

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        property_id: id, type,
        full_name: form.full_name, email: form.email, phone: form.phone,
        company: form.company_legal_name || null,
        requested_surface: form.requested_surface ? parseInt(form.requested_surface) : null,
        start_date: form.start_date || null,
        duration_months: form.duration_months ? parseInt(form.duration_months) : null,
        message: form.message || null,
        preferred_visit_date: form.preferred_visit_date || null,
        flow_mode: flowMode,
        activity_type: form.activity_type || null,
        activity_details: form.activity_details || null,
        bilan: flowMode === "full" ? {
          revenue: form.bilan_revenue ? parseFloat(form.bilan_revenue) : null,
          net_profit: form.bilan_profit ? parseFloat(form.bilan_profit) : null,
          employees: form.bilan_employees ? parseInt(form.bilan_employees) : null,
          fiscal_year: form.bilan_fiscal_year || null,
          notes: form.bilan_notes || null,
          documents: form.bilan_documents,
        } : null,
        company_info: {
          siret: form.company_siret || null,
          legal_name: form.company_legal_name || null,
          director_name: form.company_director || null,
          address: form.company_address || null,
          postal_code: form.company_postal || null,
          city: form.company_city || null,
        },
      };
      const { data: reservation } = await api.post("/reservations", payload);
      // Track conversion (visit_request + form_submit) — fired BEFORE redirect to checkout so it reliably reaches GA/Ads
      trackFormSubmit("reservation", { type, property_id: property?.id, property_title: property?.title });
      trackVisitRequest({ type, property_id: property?.id, property_title: property?.title });
      if (type === "paid") {
        const { data } = await api.post("/payments/checkout", { reservation_id: reservation.id, origin_url: window.location.origin });
        window.location.href = data.url;
        return;
      }
      toast.success(t("reservation.success"));
      navigate("/locaux");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur lors de l'envoi");
    } finally { setSubmitting(false); }
  };

  if (!property) return <div className="p-24 text-center font-mono text-sm">{t("global.loading")}</div>;

  const STEP_LABEL = {
    mode: "Type de demande",
    activity: "Activité",
    bilan: "Bilan & documents",
    company: "Entreprise",
    review: "Coordonnées & récap",
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <Navbar />
      <Toaster richColors position="top-right" />
      <section className="max-w-5xl mx-auto px-6 md:px-8 py-12">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFC107] mb-3">— {property.title}</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight mb-2">{t("reservation.title")}</h1>
        <p className="text-slate-600 mb-8">{t("reservation.subtitle")}</p>

        {/* Stepper */}
        <div className="flex items-center justify-between gap-2 mb-10 overflow-x-auto" data-testid="reservation-stepper">
          {visibleSteps.map((s, i) => {
            const stepClass = i < step
              ? "bg-slate-900 text-white"
              : i === step
              ? "bg-[#FFC107] text-slate-900"
              : "bg-white text-slate-500";
            return (
              <div key={s} className="flex items-center gap-2 min-w-fit">
                <div className={`w-8 h-8 border border-slate-900 flex items-center justify-center text-xs font-bold ${stepClass}`}>
                  {i < step ? <Check className="w-4 h-4" /> : i + 1}
                </div>
                <span className={`text-xs font-bold uppercase tracking-wider ${i === step ? "text-slate-900" : "text-slate-400"}`}>{STEP_LABEL[s]}</span>
                {i < visibleSteps.length - 1 && <div className="w-6 h-px bg-slate-300 mx-1"></div>}
              </div>
            );
          })}
        </div>

        <div className="bg-white border border-slate-900/15 p-8 md:p-10" data-testid={`reservation-step-${currentStep}`}>
          {/* STEP — Mode */}
          {currentStep === "mode" && (
            <div className="space-y-8">
              <div>
                <h2 className="font-display font-bold text-2xl mb-2">Que souhaitez-vous faire ?</h2>
                <div className="grid md:grid-cols-3 gap-3 mt-4">
                  {Object.entries(TYPES).map(([k, t2]) => {
                    const active = type === k;
                    const Icon = t2.icon;
                    return (
                      <button key={k} onClick={() => setType(k)} data-testid={`type-${k}`}
                        className={`text-left p-5 border transition-all ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-900/20 hover:border-slate-900"}`}>
                        <div className="flex items-center justify-between mb-3">
                          <Icon className="w-5 h-5" />
                          {active && <Check className="w-5 h-5 text-[#FFC107]" />}
                        </div>
                        <div className="font-display font-bold text-base mb-1">{t2.label}</div>
                        <div className={`text-xs ${active ? "text-slate-300" : "text-slate-500"}`}>{t2.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <h2 className="font-display font-bold text-2xl mb-2">Niveau de détail du dossier</h2>
                <p className="text-sm text-slate-500 mb-4">Choisissez « complet » pour accélérer la validation (notamment pour une réservation payante).</p>
                <div className="grid md:grid-cols-2 gap-3">
                  <button onClick={() => setFlowMode("quick")} data-testid="mode-quick"
                    className={`text-left p-5 border transition-all ${flowMode === "quick" ? "bg-[#FFC107] border-slate-900" : "bg-white border-slate-900/20 hover:border-slate-900"}`}>
                    <div className="font-display font-bold text-base mb-1">Dossier rapide</div>
                    <div className="text-xs text-slate-700">Activité + entreprise + coordonnées (3 étapes).</div>
                  </button>
                  <button onClick={() => setFlowMode("full")} data-testid="mode-full"
                    className={`text-left p-5 border transition-all ${flowMode === "full" ? "bg-[#FFC107] border-slate-900" : "bg-white border-slate-900/20 hover:border-slate-900"}`}>
                    <div className="font-display font-bold text-base mb-1">Dossier complet</div>
                    <div className="text-xs text-slate-700">Inclut bilan financier & documents (K-bis, bilan PDF) — 4 étapes.</div>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* STEP — Activity */}
          {currentStep === "activity" && (
            <div className="space-y-5">
              <div className="flex items-center gap-3 mb-2"><Briefcase className="w-6 h-6 text-[#FFC107]" /><h2 className="font-display font-bold text-2xl">Votre activité</h2></div>
              <Field label="Type d'activité *">
                <select value={form.activity_type} onChange={set("activity_type")} className="input" data-testid="rf-activity-type">
                  <option value="">— Sélectionnez —</option>
                  {activityTypes.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
                </select>
                {form.activity_type && activityTypes.find(a => a.name === form.activity_type)?.description && (
                  <div className="text-xs text-slate-500 mt-2">{activityTypes.find(a => a.name === form.activity_type).description}</div>
                )}
              </Field>
              <Field label="Détail de votre activité (optionnel)">
                <textarea rows={4} value={form.activity_details} onChange={set("activity_details")} className="input resize-none" data-testid="rf-activity-details" placeholder="Décrivez ce que vous prévoyez de faire dans le local..." />
              </Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Surface souhaitée (m²)">
                  <input type="number" min={property.surface_min} max={property.surface_max} value={form.requested_surface} onChange={set("requested_surface")} className="input" placeholder={`${property.surface_min}–${property.surface_max}`} data-testid="rf-surface" />
                </Field>
                <Field label="Durée envisagée (mois)">
                  <input type="number" min="1" value={form.duration_months} onChange={set("duration_months")} className="input" data-testid="rf-duration" />
                </Field>
              </div>
            </div>
          )}

          {/* STEP — Bilan */}
          {currentStep === "bilan" && (
            <div className="space-y-5">
              <div className="flex items-center gap-3 mb-2"><FileCheck2 className="w-6 h-6 text-[#FFC107]" /><h2 className="font-display font-bold text-2xl">Bilan financier (optionnel)</h2></div>
              <p className="text-sm text-slate-500">Ces informations accélèrent la validation. Pour une réservation payante, elles sont fortement recommandées.</p>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Chiffre d'affaires (€)"><input type="number" value={form.bilan_revenue} onChange={set("bilan_revenue")} className="input" data-testid="rf-revenue" /></Field>
                <Field label="Résultat net (€)"><input type="number" value={form.bilan_profit} onChange={set("bilan_profit")} className="input" data-testid="rf-profit" /></Field>
                <Field label="Effectif"><input type="number" min="0" value={form.bilan_employees} onChange={set("bilan_employees")} className="input" data-testid="rf-employees" /></Field>
                <Field label="Exercice fiscal (ex: 2024)"><input value={form.bilan_fiscal_year} onChange={set("bilan_fiscal_year")} className="input" data-testid="rf-fiscal-year" /></Field>
              </div>
              <Field label="Notes complémentaires">
                <textarea rows={3} value={form.bilan_notes} onChange={set("bilan_notes")} className="input resize-none" data-testid="rf-bilan-notes" />
              </Field>
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Documents (K-bis, bilan PDF, etc.)</div>
                <label className="block border-2 border-dashed border-slate-900/30 p-6 text-center cursor-pointer hover:border-slate-900 transition-colors" data-testid="rf-upload-zone">
                  <Upload className="w-6 h-6 mx-auto mb-2 text-slate-400" />
                  <span className="text-sm font-semibold">Ajouter un document (PDF, JPG, PNG · max 8 Mo)</span>
                  <input type="file" accept="application/pdf,image/*" onChange={e => uploadDoc(e.target.files[0])} className="hidden" />
                </label>
                {form.bilan_documents.length > 0 && (
                  <div className="space-y-2 mt-3">
                    {form.bilan_documents.map((d, i) => (
                      <div key={i} className="flex items-center justify-between border border-slate-900/15 p-3 text-sm" data-testid={`rf-doc-${i}`}>
                        <span className="flex items-center gap-2 truncate"><FileText className="w-4 h-4 shrink-0" /><span className="truncate">{d.name}</span></span>
                        <button type="button" onClick={() => removeDoc(i)} className="text-red-600 shrink-0"><X className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP — Company */}
          {currentStep === "company" && (
            <div className="space-y-5">
              <div className="flex items-center gap-3 mb-2"><Building className="w-6 h-6 text-[#FFC107]" /><h2 className="font-display font-bold text-2xl">Coordonnées de l'entreprise</h2></div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="SIRET *"><input required value={form.company_siret} onChange={set("company_siret")} className="input" maxLength="14" placeholder="14 chiffres" data-testid="rf-siret" /></Field>
                <Field label="Raison sociale *"><input required value={form.company_legal_name} onChange={set("company_legal_name")} className="input" data-testid="rf-legal-name" /></Field>
                <Field label="Dirigeant"><input value={form.company_director} onChange={set("company_director")} className="input" data-testid="rf-director" /></Field>
                <Field label="Adresse"><input value={form.company_address} onChange={set("company_address")} className="input" data-testid="rf-address" /></Field>
                <Field label="Code postal"><input value={form.company_postal} onChange={set("company_postal")} className="input" data-testid="rf-postal" /></Field>
                <Field label="Ville"><input value={form.company_city} onChange={set("company_city")} className="input" data-testid="rf-city" /></Field>
              </div>
            </div>
          )}

          {/* STEP — Review */}
          {currentStep === "review" && (
            <div className="space-y-5">
              <div className="flex items-center gap-3 mb-2"><Check className="w-6 h-6 text-[#FFC107]" /><h2 className="font-display font-bold text-2xl">Vos coordonnées</h2></div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Nom complet *"><input required value={form.full_name} onChange={set("full_name")} className="input" data-testid="rf-name" /></Field>
                <Field label="Email *"><input required type="email" value={form.email} onChange={set("email")} className="input" data-testid="rf-email" /></Field>
                <Field label="Téléphone *"><input required value={form.phone} onChange={set("phone")} className="input" data-testid="rf-phone" /></Field>
                {type === "visit" ? (
                  <Field label="Date de visite souhaitée"><input type="date" value={form.preferred_visit_date} onChange={set("preferred_visit_date")} className="input" /></Field>
                ) : (
                  <Field label="Date d'entrée souhaitée"><input type="date" value={form.start_date} onChange={set("start_date")} className="input" /></Field>
                )}
              </div>
              <Field label="Message libre">
                <textarea rows={4} value={form.message} onChange={set("message")} className="input resize-none" data-testid="rf-message" placeholder="Précisions, contraintes, questions..." />
              </Field>

              {/* Récap */}
              <div className="bg-slate-50 border border-slate-900/10 p-5 mt-6 text-sm space-y-2">
                <div className="font-display font-bold text-base mb-2">Récapitulatif</div>
                <div><strong>Type :</strong> {TYPES[type]?.label}</div>
                <div><strong>Activité :</strong> {form.activity_type || "—"}</div>
                <div><strong>Entreprise :</strong> {form.company_legal_name} {form.company_siret && `(SIRET ${form.company_siret})`}</div>
                {flowMode === "full" && form.bilan_revenue && <div><strong>CA :</strong> {parseFloat(form.bilan_revenue).toLocaleString("fr-FR")} €</div>}
                {flowMode === "full" && form.bilan_documents.length > 0 && <div><strong>Documents :</strong> {form.bilan_documents.length}</div>}
              </div>

              {type === "paid" && (
                <div className="bg-[#FFC107]/10 border-l-4 border-[#FFC107] p-4 text-sm">
                  {t("reservation.deposit_note")}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Nav */}
        <div className="flex items-center justify-between mt-8">
          <button onClick={prev} disabled={step === 0} data-testid="rf-prev"
            className="btn-brutal bg-white border border-slate-900 px-5 py-3 font-bold uppercase tracking-wider text-sm flex items-center gap-2 disabled:opacity-30">
            <ArrowLeft className="w-4 h-4" /> Précédent
          </button>
          {step < visibleSteps.length - 1 ? (
            <button onClick={next} disabled={!canNext()} data-testid="rf-next"
              className="btn-brutal btn-brutal-solar px-6 py-3 font-bold uppercase tracking-wider text-sm flex items-center gap-2 disabled:opacity-50">
              Suivant <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            (() => {
              let submitLabel;
              if (submitting) submitLabel = t("global.cta_sending");
              else if (type === "paid") submitLabel = t("reservation.submit_paid");
              else submitLabel = t("reservation.submit_other");
              return (
                <button onClick={submit} disabled={submitting || !canNext()} data-testid="rf-submit"
                  className="btn-brutal btn-brutal-solar px-6 py-3 font-bold uppercase tracking-wider text-sm flex items-center gap-2 disabled:opacity-50">
                  {submitLabel}
                  {!submitting && <ArrowRight className="w-4 h-4" />}
                </button>
              );
            })()
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 block">{label}</span>
    {children}
  </label>
);
